import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getUserDetails,
  updateUser,
  deleteUser,
  logoutUser,
} from "../util/wordywhiz";
import { useAuth } from "../context/AuthContext";

const FIELDS = [
  { label: "First Name", name: "first_name", type: "text" },
  { label: "Last Name", name: "last_name", type: "text" },
  { label: "Email", name: "email", type: "email" },
  { label: "Username", name: "username", type: "text" },
];

function ManageAccount() {
  const navigate = useNavigate();
  const { setLoggedOut } = useAuth();

  const [userDetails, setUserDetails] = useState({
    first_name: "",
    last_name: "",
    email: "",
    username: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    getUserDetails()
      .then((details) => setUserDetails(details))
      .catch(() => setError("Could not load account details."))
      .finally(() => setIsLoading(false));
  }, []);

  async function updateHandler(e) {
    e.preventDefault();
    setIsSaving(true);
    setError("");
    setSuccessMsg("");
    try {
      await updateUser(userDetails);
      setSuccessMsg("Account updated successfully.");
    } catch {
      setError("Could not update account. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }

  async function deleteHandler() {
    setShowDeleteModal(false);
    setIsSaving(true);
    try {
      await deleteUser();
      setLoggedOut();
      navigate("/wordy-whiz/login");
    } catch {
      setError("Could not delete account. Please try again.");
      setIsSaving(false);
    }
  }

  async function logoutHandler() {
    await logoutUser();
    setLoggedOut();
    navigate("/wordy-whiz/login");
  }

  return (
    <div className="auth-page">
      <div className="study-header">
        <p className="study-eyebrow">Wordy Whiz</p>
        <h1>Manage Account</h1>
      </div>

      {error && <p className="auth-error">{error}</p>}
      {successMsg && <p className="auth-success">{successMsg}</p>}

      {isLoading || isSaving ? (
        <div className="dict-spinner" />
      ) : (
        <form className="auth-form" onSubmit={updateHandler}>
          {FIELDS.map(({ label, name, type }) => (
            <label className="auth-field" key={name}>
              <span className="ww-label">{label}</span>
              <input
                className="auth-input"
                type={type}
                autoCapitalize="none"
                value={userDetails[name] || ""}
                onChange={(e) =>
                  setUserDetails((c) => ({ ...c, [name]: e.target.value }))
                }
              />
            </label>
          ))}

          <div className="auth-actions">
            <button type="submit" className="ww-btn ww-btn--primary">Update Account</button>
            <button type="button" className="ww-btn ww-btn--secondary" onClick={logoutHandler}>
              Log Out
            </button>
          </div>
          <div className="auth-danger-zone">
            <button
              type="button"
              className="ww-btn ww-btn--destructive"
              onClick={() => setShowDeleteModal(true)}
            >
              Delete Account
            </button>
          </div>
        </form>
      )}

      {showDeleteModal && (
        <div className="auth-modal-backdrop" onClick={() => setShowDeleteModal(false)}>
          <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Delete Account</h2>
            <p>Are you sure you want to permanently delete your account? This cannot be undone.</p>
            <div className="auth-modal__actions">
              <button className="ww-btn ww-btn--secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button className="ww-btn ww-btn--destructive" onClick={deleteHandler}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageAccount;
